/**
 * @file SearchableMenuList.tsx
 * @brief A searchable list for inside a dropdown menu: title, search, rows.
 *
 * @details
 * DES (30-09-2026), on the Boardrooms "Filter by tag" menu: "yeh component
 * searchable banao, we will use this later for pickers and stuff". One kit
 * for every small picker that opens under a button:
 * - An uppercase section title, then a round search field (focused on open).
 * - Rows: optional icon, label, optional description, a tick when selected.
 * - `multiple`: rows toggle and the menu stays open; otherwise a pick closes.
 * - Filters on label + description + `keywords`, case-insensitive; "No
 *   matches" when nothing fits, `emptyText` when there are no items at all.
 *
 * Render it inside `DropdownMenuContent`. Typing in the field must not reach
 * Radix's typeahead (which would jump focus to a matching row), so the field
 * stops key events except Escape and Tab. ArrowDown moves into the rows;
 * Enter picks the first match.
 *
 * @author DES
 * @date 30-09-2026
 */

import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Check, Search, X } from "lucide-react";
import { DropdownMenuItem, DropdownMenuLabel } from "@/components/ui/DropdownMenu";
import { cn } from "@/shared/theme/cn";

export type SearchableMenuItem = {
  value: string;
  label: string;
  /** A second line, e.g. an email under a name. */
  description?: string;
  icon?: ReactNode;
  /** Extra text that matches the search but is not shown. */
  keywords?: string;
  disabled?: boolean;
  /** A control at the row's end (e.g. a Share toggle). Its clicks do not
   *  pick the row. */
  trailing?: ReactNode;
  /** (04-10-2026) A small heading is drawn above the first row of each
   *  group, e.g. "Recent" / "All languages". Rows of a group stay together. */
  group?: string;
};

export function SearchableMenuList({
  title,
  items,
  selected,
  onSelect,
  multiple = false,
  searchPlaceholder = "Search",
  emptyText = "Nothing to choose from yet.",
  noMatchText = "No matches",
  maxHeight = 320,
  query: queryProp,
  onQueryChange,
  loading = false,
  header,
  footer,
  action,
}: {
  title?: string;
  items: SearchableMenuItem[];
  /** Values that show a tick. */
  selected?: string[];
  /** Called with the picked value (toggle it yourself when `multiple`). */
  onSelect: (value: string) => void;
  /** Rows toggle and the menu stays open. */
  multiple?: boolean;
  searchPlaceholder?: string;
  emptyText?: ReactNode;
  noMatchText?: ReactNode;
  /** The row list scrolls past this (px). */
  maxHeight?: number;
  /** Server-side search: pass the query and its setter; the list is then
   *  shown as given (the caller filters), and the field stays visible even
   *  when a search returns nothing. */
  query?: string;
  onQueryChange?: (q: string) => void;
  /** Rows are loading (first load or a search round-trip). */
  loading?: boolean;
  /** Content between the search and the rows (e.g. "add by email"). While
   *  it renders, the "No matches" line is not shown. */
  header?: ReactNode;
  /** Content under the rows (e.g. a "Clear selection" item). */
  footer?: ReactNode;
  /** A control under the title, shown even when there is nothing to choose
   *  yet (DES 12-10-2026: the agenda picker's "Write new agenda"). */
  action?: ReactNode;
}) {
  const [localQuery, setLocalQuery] = useState("");
  const controlled = queryProp !== undefined && onQueryChange !== undefined;
  const query = controlled ? queryProp : localQuery;
  const setQuery = controlled ? onQueryChange : setLocalQuery;
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Radix focuses the content on open; take it for the field a tick later.
  useEffect(() => {
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, []);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || controlled) return items;
    return items.filter((it) =>
      [it.label, it.description, it.keywords].filter(Boolean).join(" ").toLowerCase().includes(q),
    );
  }, [items, query, controlled]);
  const picked = new Set(selected ?? []);

  return (
    <>
      {title ? (
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
          {title}
        </DropdownMenuLabel>
      ) : null}
      {action}

      {items.length === 0 && !query && !loading && !controlled ? (
        <p className="px-2 pb-2 text-sm text-muted-foreground">{emptyText}</p>
      ) : (
        <>
          <div className="px-1 pb-1.5">
            <div className="flex h-9 items-center gap-2 rounded-full bg-field px-3">
              <Search className="h-4 w-4 shrink-0 text-brand" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown") {
                    // Into the rows: focus the first one; Radix's arrows
                    // take over from there.
                    e.preventDefault();
                    e.currentTarget
                      .closest('[role="menu"]')
                      ?.querySelector<HTMLElement>('[role="menuitem"]:not([data-disabled])')
                      ?.focus();
                    return;
                  }
                  if (e.key === "Enter") {
                    // The first match, as a picker's search usually does.
                    e.preventDefault();
                    const first = shown.find((it) => !it.disabled);
                    if (first) onSelect(first.value);
                  }
                  // Keep typing out of the menu's typeahead; Escape and Tab
                  // still reach the menu.
                  if (e.key !== "Escape" && e.key !== "Tab") e.stopPropagation();
                }}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
              {query ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => {
                    setQuery("");
                    inputRef.current?.focus();
                  }}
                  className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </div>
          </div>

          {header ? (
            // Fields in here (e.g. a guest's name) type freely: keep keys
            // out of the menu's typeahead, as the search field does.
            <div
              onKeyDown={(e) => {
                if (e.key !== "Escape" && e.key !== "Tab") e.stopPropagation();
              }}
            >
              {header}
            </div>
          ) : null}

          <div className="overflow-y-auto" style={{ maxHeight }}>
            {loading && shown.length === 0 ? (
              <p className="px-2 py-2 text-sm text-muted-foreground">Loading…</p>
            ) : shown.length === 0 && header ? null : shown.length === 0 ? (
              <p className="px-2 py-2 text-sm text-muted-foreground">
                {query ? noMatchText : emptyText}
              </p>
            ) : (
              shown.map((it, i) => {
                const on = picked.has(it.value);
                const heading =
                  it.group && it.group !== shown[i - 1]?.group ? (
                    <DropdownMenuLabel className="px-2 pb-1 pt-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                      {it.group}
                    </DropdownMenuLabel>
                  ) : null;
                return (
                  <Fragment key={it.value}>
                  {heading}
                  <DropdownMenuItem
                    key={it.value}
                    disabled={it.disabled}
                    onSelect={(e) => {
                      if (multiple) e.preventDefault();
                      onSelect(it.value);
                    }}
                    className={cn(on && "text-brand focus:text-brand")}
                  >
                    {it.icon ? (
                      <span className="mr-2 flex shrink-0 items-center">{it.icon}</span>
                    ) : null}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{it.label}</span>
                      {it.description ? (
                        <span className="block truncate text-xs text-muted-foreground">
                          {it.description}
                        </span>
                      ) : null}
                    </span>
                    {it.trailing ? (
                      <span
                        className="ml-2 flex shrink-0 items-center"
                        onClick={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        onPointerUp={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        {it.trailing}
                      </span>
                    ) : null}
                    {on ? <Check className="ml-2 h-4 w-4 shrink-0" /> : null}
                  </DropdownMenuItem>
                  </Fragment>
                );
              })
            )}
          </div>
          {footer}
        </>
      )}
    </>
  );
}
